<template>
  <div>
    <Loader :messageSelector="messageSelector" :overlay="overlay" />

    <v-form ref="form" v-model="valid">
      <fieldset>
        <legend v-if="!isDroitsAcquis" class="text-h6 text-sm-h5">Attestation et serment d'office</legend>
        <legend v-else class="text-h6 text-sm-h5">Attestation</legend>

        <v-checkbox
          v-model="attestationVeracite.identiteEtRenseignements"
          :rules="rules.identiteEtRenseignements"
          color="primary"
          hide-details
          required
        >
          <template v-slot:label>
            <div class="ml-5">
              Je certifie que je suis bien <strong>{{ nomDuDemandant }}</strong> ou son représentant légal et que tous les renseignements
              indiqués sur ce formulaire électronique sont exacts et complets.
            </div>
          </template>
        </v-checkbox>

        <v-checkbox
          v-model="attestationVeracite.modaliteDemandeAdmission"
          :rules="rules.modaliteDemandeAdmission"
          color="primary"
          hide-details
          required
        >
          <template v-slot:label>
            <div v-if="!isDroitsAcquis" class="ml-5">
              J'affirme avoir pris connaissance des modalités relatives à la cotisation professionnelle et le tableau des frais ainsi que
              l'assurance responsabilité professionnelle.
            </div>
            <div v-else class="ml-5">
              J'affirme avoir pris connaissance des modalités des frais d'adhésion et le mode de paiement dans la foire aux questions.
            </div>
          </template>
        </v-checkbox>

        <section v-if="!isDroitsAcquis">
          <v-checkbox
            v-model="attestationVeracite.honneteteFideliteJusticeEtSecret"
            :rules="rules.honneteteFideliteJusticeEtSecret"
            color="primary"
            hide-details
            required
          >
            <template v-slot:label>
              <div class="ml-5">
                Je m'engage à remplir avec honnêteté, fidélité et justice les devoirs imposés au travailleur social et/ou au thérapeute
                conjugal et familial par le Code des professions et les règlements de l'OTSTCFQ, notamment à préserver le secret quant aux
                renseignements de nature confidentielle qui viendront à ma connaissance dans l'exercice de ma profession.
              </div>
            </template>
          </v-checkbox>

          <v-checkbox
            v-model="attestationVeracite.validiteDocuments"
            :rules="rules.validiteDocuments"
            color="primary"
            hide-details
            required
          >
            <template v-slot:label>
              <div class="ml-5">Je certifie que les documents fournis sont valides et authentiques.</div>
            </template>
          </v-checkbox>

          <v-checkbox
            v-model="attestationVeracite.fausseDeclaration"
            :rules="rules.fausseDeclaration"
            color="primary"
            hide-details
            required
          >
            <template v-slot:label>
              <div class="ml-5">
                Je comprends que toute fausse déclaration de ma part entraîne le rejet immédiat de ma demande et qu'une demande acceptée
                sous de fausses déclarations devient nulle.
              </div>
            </template>
          </v-checkbox>
        </section>

        <v-checkbox v-model="attestationVeracite.formationContinue" :rules="rules.formationContinue" color="primary" hide-details required>
          <template v-slot:label>
            <div class="ml-5">
              Je m'engage à maintenir l'information concernant ma déclaration de formation continue obligatoire exacte et à jour dans mon
              Espace formation, pour chaque période prenant fin le 31 mars des années paires.
            </div>
          </template>
        </v-checkbox>

        <v-checkbox v-model="verifierExactitude" :rules="rules.verifierExactitude" color="primary" hide-details required>
          <template v-slot:label>
            <div class="ml-5">
              Je comprends que l'Ordre se donne le droit en cours d'année, de vérifier l'exactitude des informations déclarées dans le
              formulaire d'inscription annuelle ainsi que dans mon profil en ligne.
            </div>
          </template>
        </v-checkbox>
      </fieldset>

      <!-- Conditionnel a Autorisation de Pratique - Autorisation special -->
      <fieldset v-if="hasAutorisationSpecial">
        <legend class="text-h6 text-sm-h5">Autorisation spéciale</legend>
        <div>
          <v-checkbox
            v-model="attestationVeracite.exercerAuxConditionsEtNormes"
            :rules="rules.exercerAuxConditionsEtNormes"
            color="primary"
            hide-details
            required
          >
            <template v-slot:label>
              <div class="ml-5">
                <p>JE M'ENGAGE À :</p>
                <ol class="c" style="margin-left: 16px">
                  <li>Exercer aux conditions et modalités prévues par l'autorisation spéciale.</li>
                  <li>
                    Porter le titre de T.S. et d'exercer les activités réservées autorisées pour la durée mentionnée par l'autorisation
                    spéciale
                  </li>
                  <li>Me conformer aux normes de pratique et au code de déontologie de la profession.</li>
                </ol>
              </div>
            </template>
          </v-checkbox>
        </div>
      </fieldset>

      <!-- Conditionnel a Autorisation de Pratique - Étudiant -->
      <fieldset v-if="isStudent">
        <legend class="text-h6 text-sm-h5">Étudiant</legend>
        <v-checkbox
          v-model="attestationVeracite.contactEtablissementEtSuperviseur"
          :rules="rules.contactEtablissementEtSuperviseur"
          color="primary"
          hide-details
          required
        >
          <template v-slot:label>
            <div class="ml-5">
              J'autorise mon établissement d'enseignement responsable du programme auquel j'ai été admis(e) à informer l'Ordre des
              changements à mon statut d'étudiant(e), ainsi qu'à mes renseignements personnels notés à la section « identification ».
              J'autorise l'Ordre à communiquer avec mon superviseur au besoin.
            </div>
          </template>
        </v-checkbox>
      </fieldset>

      <!-- Conditionnel a Autorisation de Pratique - Médiateur familiale -->
      <fieldset v-if="isMediateur">
        <legend class="text-h6 text-sm-h5">Médiation avec engagement ou Réactivation</legend>
        <div>
          <v-checkbox v-model="attestationVeracite.suivreFormations" :rules="rules.suivreFormations" color="primary" hide-details required>
            <template v-slot:label>
              <div class="ml-5">
                <p>
                  Je m'engage à suivre les 45 h de formation complémentaire requise pour rencontrer les exigences du Règlement et consistant
                  en un approfondissement des mêmes sujets que la formation de base (sujets reliés à la séparation, au divorce ou à la
                  nullité du mariage), soit :
                </p>
                <!-- todo: supprimer la class c et retirer le list-style des <li> ? -->
                <ol class="c" style="margin-left: 16px">
                  <li>15 h de processus de médiation</li>
                  ET
                  <li>pour les non-juristes : 30 h sur les aspects économiques, égaux et fiscaux</li>
                </ol>
                Je m'engage à
                <ol class="c" style="margin-left: 16px">
                  <li>
                    compléter deux mandats de médiation globale (comportant quatre objets et au moins quatre séances de supervision en cours
                    de mandat)
                  </li>
                  <li>compléter trois mandats de médiation (comportant au moins trois séances de supervision en cours de mandat)</li>
                  <li>compléter cinq mandats de médiation (comportant au moins une séance de supervision en cours de mandat)</li>
                  <li>
                    que parmi les mandats supervisés soumis, au moins cinq se soient terminés par une entente portant sur tous les objets
                    et, pour ces mandats, avoir traité au moins deux fois les quatre objets de la médiation
                  </li>
                </ol>
              </div>
            </template>
          </v-checkbox>
        </div>
      </fieldset>
    </v-form>

    <div class="text-center mt-6 d-flex justify-space-between">
      <v-btn color="primary" @click="back()">Retour</v-btn>
      <v-btn :disabled="!valid" color="primary" @click="saveAndValidate()">Procéder au paiement</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import Loader from "../Loader"
import NavigationPageFormulaireInscriptionMixin from "@/mixins/navigationPageFormulaireInscriptionMixin"
import { isDroitsAcquis } from "@/utils/autorisationHelper"

export default {
  mixins: [NavigationPageFormulaireInscriptionMixin],
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  components: {
    Loader: Loader,
  },
  data() {
    return {
      messageSelector: "",
      overlay: false,
      valid: true,
      verifierExactitude: false,
      rules: {
        modaliteDemandeAdmission: [(v) => !!v || "Vous devez accepter ces termes"],
        identiteEtRenseignements: [(v) => !!v || "Vous devez accepter ces termes"],
        honneteteFideliteJusticeEtSecret: [(v) => !!v || "Vous devez accepter ces termes"],
        validiteDocuments: [(v) => !!v || "Vous devez accepter ces termes"],
        fausseDeclaration: [(v) => !!v || "Vous devez accepter ces termes"],
        exercerAuxConditionsEtNormes: [(v) => !!v || "Vous devez accepter ces termes"],
        contactEtablissementEtSuperviseur: [(v) => !!v || "Vous devez accepter ces termes"],
        suivreFormations: [(v) => !!v || "Vous devez accepter ces termes"],
        verifierExactitude: [(v) => !!v || "Vous devez accepter ces termes"],
        formationContinue: [(v) => !!v || "Vous devez accepter ces termes"],
      },
    }
  },
  computed: {
    ...mapGetters({
      attestationVeracite: "AttestationVeracites/GET_ATTESTATIONSVERACITES",
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      getAutorisation: "AutorisationDePratique/GET_AUTORISATIONSDEPRATIQUE",
      getFormations: "FormationInitiale/GET_FORMATIONS",
      renseignementsGeneraux: "RenseignementsGeneraux/getRenseignementsGeneraux",
      factureURL: "Facture/GET_FACTUREURL",
      statutEmploi: "StatutEmploi/getStatutEmploi",
    }),
    hasAutorisationSpecial: function () {
      return (
        this.getAutorisation.find((a) => a.categorieAutorisation.id == this.$catalogues.categorieAutorisation.autorisationSpeciale) ||
        this.getFormations.find(
          (f) => f.formationUniversitaireEnTravailSocial.id == process.env.VUE_APP_FORMATION_DIPLOME_AUTREAUTORISATIONSPECIALE
        )
      )
    },
    isStudent: function () {
      return this.getAutorisation.find((a) => a.categorieAutorisation.id == this.$catalogues.categorieAutorisation.registreDesEtudiants)
    },
    isMediateur: function () {
      return this.getAutorisation.find((a) => a.categorieAutorisation.id == this.$catalogues.categorieAutorisation.mediateurFamilial)
    },
    requiredFieldSelected: function () {
      let result =
        (this.isDroitsAcquis && this.attestationVeracite.identiteEtRenseignements) ||
        (this.attestationVeracite.identiteEtRenseignements &&
          this.attestationVeracite.validiteDocuments &&
          this.attestationVeracite.fausseDeclaration)

      if (!this.isDroitsAcquis && result === true) {
        result = this.attestationVeracite.honneteteFideliteJusticeEtSecret === true
      }

      if (this.hasAutorisationSpecial && result === true) {
        result = this.attestationVeracite.exercerAuxConditionsEtNormes
      }

      if (this.isStudent && result === true) {
        result = this.attestationVeracite.contactEtablissementEtSuperviseur
      }

      if (this.isMediateur) {
        result = this.attestationVeracite.suivreFormations
      }

      return result
    },
    nomDuDemandant: function () {
      return this.renseignementsGeneraux.firstName + " " + this.renseignementsGeneraux.lastName
    },
    isDroitsAcquis() {
      return isDroitsAcquis(this.getAutorisation)
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    ...mapActions({
      updateAttestationVeracites: "AttestationVeracites/updateAttestationsVeracites",
      getAttestationVeracitesByDemandeInscriptionId: "AttestationVeracites/getAttestationsVeracitesByDemandeInscriptionId",
      envoiLien: "DemandeAdmission/envoiLien",
      getAutorisationDePratiqueByDemandeInscriptionId: "AutorisationDePratique/getAutorisationDePratiqueByDemandeInscriptionId",
      getRenseignementsGenerauxFromInscription: "RenseignementsGeneraux/getRenseignementsGenerauxFromInscription",
      validerLaDemandeInscription: "DemandeInscription/validerLaDemandeInscription",
      demandeTerminee: "DemandeInscription/demandeTerminee",
      getLienFactureByDemandeInscriptionId: "Facture/getLienFactureByDemandeInscriptionId",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        if (this.demandeInscriptionId) {
          await this.getAttestationVeracitesByDemandeInscriptionId(this.demandeInscriptionId)
          await this.getAutorisationDePratiqueByDemandeInscriptionId(this.demandeInscriptionId)
          await this.getRenseignementsGenerauxFromInscription(this.demandeInscriptionId)
        }

        this.overlay = false
      } catch (erreur) {
        console.log(erreur)
      }
    },
    async saveAndValidate() {
      this.messageSelector = "Facture"
      this.overlay = true
      await this.save()
      this.overlay = false
    },
    async save() {
      this.$refs.form.validate()
      if (this.valid && this.attestationVeracite && this.requiredFieldSelected) {
        this.messageSelector = "Creation"
        this.overlay = true

        await this.updateAttestationVeracites(this.attestationVeracite)
        try {
          //await this.validerLaDemandeInscription()
          //await this.getLienFactureByDemandeInscriptionId(this.demandeInscriptionId);
          await this.demandeTerminee()
        } catch (err) {
          console.log(err.message)
          this.$router.push({
            name: "InternalError",
            params: {
              message: "Une erreur est survenue lors de la validation de votre demande d'inscription. <br/> Veuillez contacter l'Ordre.",
            },
          })
        }
        this.redirect()
      }
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item,
    redirect() {
      this.$router.push({
        name: "DemandeInscriptionRenouvellement/DemandeRenouvellementEnregistree",
      })

      // NE PAS SUPPRIMER - a réactiver quand les problèmes de perf produit seront résolus
      //if (!this.isPaiementCarte) {
      //  this.$router.push({ name: "DemandeInscription/RedirectionFacture" });
      //} else {
      //  window.location.replace(this.factureURL);
      //}
    },
  },
}
</script>

<style></style>